import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuPencil, LuPlus, LuSwords } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteCompetitorProfile,
  getBusinessFeatures,
  getCompetitorProfiles,
  getCustomerProfileSubsets,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import { CompetitorProfileDialog } from "../../../components/competitor-profile-dialog";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import PageBody from "../../../components/page-body";
import { useLoading, useToaster } from "../../../hooks";
import {
  BusinessFeature,
  CompetitorProfile,
  CustomerProfileSubset,
} from "../../../types/company-knowledge";

export default function CompetitorProfilesPage() {
  const { workspace: workspaceSlug } = useParams();
  const [competitors, setCompetitors] = React.useState<CompetitorProfile[]>([]);
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [features, setFeatures] = React.useState<BusinessFeature[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingCompetitor, setEditingCompetitor] =
    React.useState<CompetitorProfile | null>(null);
  const withToaster = useToaster();

  const loadCompetitors = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        // The subsets and features ride along on every load so the
        // "Applicable ICP subsets" column and the dialog's checkbox groups stay
        // in sync.
        const [competitors, subsets, features] = await Promise.all([
          getCompetitorProfiles(workspaceSlug),
          getCustomerProfileSubsets(workspaceSlug),
          getBusinessFeatures(workspaceSlug),
        ]);
        setCompetitors(competitors);
        setSubsets(subsets);
        setFeatures(features);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadCompetitors();
  }, [loadCompetitors]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteCompetitorProfile(workspaceSlug, id);
          await loadCompetitors();
        });
      });
    },
    [loadCompetitors, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<CompetitorProfile>[] = [
    {
      id: "name",
      name: "Name",
      icon: <LuSwords />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.name}</Text>,
    },
    {
      id: "competitive-niche",
      name: "How they compete",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value["competitive-niche"] || "—"}
        </Text>
      ),
    },
    {
      name: "Applicable ICP subsets",
      width: 200,
      cell: ({ value }) => {
        const names = subsets
          .filter((s) => value["subset-ids"].includes(s.id))
          .map((s) => s.name);
        return (
          <Text color="fg.muted" truncate>
            {names.length > 0 ? names.join(", ") : "All subsets"}
          </Text>
        );
      },
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit competitor"
            variant="ghost"
            onClick={() => {
              setEditingCompetitor(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.name}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = competitors.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              Competitors
            </Text>
            <Text color="fg.muted" fontSize="sm">
              Who you are up against, and where you are stronger or weaker.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingCompetitor(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Competitor
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={<IconCircle Icon={LuSwords} size="md" variant="flat" />}
              title="No Competitors"
              description="Add the competitors you want to be measured against."
            >
              <Button
                onClick={() => {
                  setEditingCompetitor(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Competitor
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={competitors}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <CompetitorProfileDialog
        competitor={editingCompetitor}
        features={features}
        isOpen={isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) setEditingCompetitor(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadCompetitors}
        subsets={subsets}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}
